import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { activity as seedActivity, projects as seedProjects, templates as seedTemplates } from '../data/projects';
import { projectStatus } from '../utils/status';
import { deliverableStage } from '../utils/deliverables';
import { normaliseTimecode } from '../utils/time';
import { applyStoredOrder, saveDeliverableOrder } from '../utils/order';
import { applyStoredTranscripts, saveTranscriptText } from '../utils/transcripts';
import { authenticateStaff, changeStaffPassword, clearStaffSession, loadStaffSession, completeStaffInvite, completeStaffPasswordReset, establishInviteSession, requestStaffPasswordReset, verifyStaffRecoveryCode, type StaffActionResult, type StaffAuthResult, type StaffSession } from '../utils/staffAuth';
import { clearClientName, loadClientName, saveClientName } from '../utils/clientIdentity';
import { slugFromPath, uniqueSlug } from '../utils/links';
import { ensureAnonSession, supabase, supabaseConfigured } from '../lib/supabase';
import { closeProjectSession, fetchApprovals, fetchChangeRequests, fetchStaffActivity, resolveChangeRequest as resolveRemoteChangeRequest } from '../utils/sharedReview';
import { fetchProjectById, fetchProjects } from '../utils/projectsSource';
import { fetchAccessToken } from '../utils/accessToken';
import { deleteDeliverableRow, deleteProjectRow, insertDeliverable, insertProject, saveDeliverableOrderRows, updateDeliverableRow, updateProjectRow } from '../utils/projectWrites';
import { assignProjectMember, removeProjectMember, updateProjectMemberRole } from '../utils/projectTeam';
import { insertApproval, insertChangeRequest, openProjectSession } from '../utils/sharedReview';
import type { Activity, ClientAlert, Deliverable, Project, ProjectTemplate, Shoot, TeamMember, TemplateDeliverable } from '../types/portal';

/** Stand-in used only before any project has loaded. */
const EMPTY_PROJECT: Project = {
  id: '',
  slug: '',
  client: '',
  name: '',
  type: '',
  description: '',
  status: '',
  nextShootDate: '',
  nextShootTime: '',
  location: '',
  clientContact: '',
  producer: '',
  updated: '',
  deliverables: [],
  shoots: [],
  team: [],
  archived: false
};
export type ViewMode = 'client' | 'portal';
export type ClientRoute = 'home' | 'project' | 'episodes' | 'episode' | 'transcript' | 'files' | 'schedule';
export type PortalRoute = 'projects' | 'project' | 'team';
export interface NewDeliverableInput {
  name: string;
  type: Deliverable['type'];
  status: Deliverable['status'];
  version: string;
  driveUrl: string;
  audioUrl: string;
  visibleToClient: boolean;
  /** Video framing. Only meaningful when the type is Video. */
  videoFormat?: Deliverable['videoFormat'];
  /** Transcript text, for Transcript items. */
  transcript?: string;
}
export interface NeedsAttention {
  reviewsWaiting: number;
  changeRequests: number;
  readyToPublish: number;
}

/** Routes. Staff sign-in and the admin area are deliberately separate URLs. */
export const CLIENT_PATH = '/client';
export const STAFF_PATH = '/staff';
export const ADMIN_PATH = '/admin';
/** Where a Supabase staff invitation link lands. */
export const STAFF_SETUP_PATH = '/staff/setup-password';
/** Where a resent staff setup link lands. Same screen. */
export const STAFF_RESET_PATH = '/staff/reset-password';
interface PortalState {
  authed: boolean;
  /** The name this browser gave when it first opened the shared project link. */
  clientName: string;
  /** Records the name and opens the project. */
  signIn: (name: string) => void;
  /** Forgets the remembered name and returns to the name prompt. */
  changeClientName: () => void;

  /** Current browser path — '/client', '/staff' or the protected '/admin'. */
  path: string;
  navigate: (next: string) => void;
  /** True on either studio route (sign-in or admin). */
  inStaffArea: boolean;

  /** Portal staff authentication, entirely separate from client access. */
  staffAuthed: boolean;
  /** True while the persisted staff session is still being verified. */
  staffChecking: boolean;
  staffUser: StaffSession | null;
  staffSignIn: (email: string, password: string) => Promise<StaffAuthResult>;
  staffSignOut: () => void;
  /** Sends a Supabase password-reset email. */
  staffResetPassword: (email: string) => Promise<StaffActionResult>;
  staffVerifyRecoveryCode: (email: string, code: string) => Promise<StaffActionResult>;
  staffCompletePasswordReset: (password: string, confirm: string) => Promise<StaffActionResult>;
  staffChangePassword: (current: string, password: string, confirm: string) => Promise<StaffActionResult>;
  /** Reads the invitation link and establishes its session. */
  staffCheckInvite: () => Promise<StaffActionResult>;
  /** Sets the first password for an invited staff member, then signs them in. */
  staffAcceptInvite: (password: string, confirm: string) => Promise<StaffAuthResult>;
  /** Returns to the Staff Access sign-in screen. */
  goStaffAccess: () => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  clientRoute: ClientRoute;
  goClient: (route: ClientRoute) => void;
  portalRoute: PortalRoute;
  goPortal: (route: PortalRoute) => void;
  projects: Project[];
  activeProjects: Project[];
  archivedProjects: Project[];
  activeProject: Project;
  openProject: (id: string) => void;
  templates: ProjectTemplate[];

  /** Portal staff previewing the live client experience. */
  previewAsClient: boolean;
  startPreview: () => void;
  endPreview: () => void;

  /** True once a project is delivered or archived — review actions retire. */
  projectComplete: boolean;
  archiveProject: () => void;
  /** Permanently deletes the open project and its own records. */
  deleteProject: () => Promise<boolean>;
  findExistingProject: (client: string, name: string) => Project | null;
  reopenProject: (id: string) => void;
  toggleVersionVisibility: (deliverableId: string, versionId: string) => void;
  contactPortal: (body: string) => void;

  /** Everything Portal has marked visible, in Portal's order, whatever the stage. */
  clientDeliverables: Deliverable[];
  reviewDeliverables: Deliverable[];
  finalDeliverables: Deliverable[];
  activeDeliverable: Deliverable | undefined;
  openDeliverable: (id: string) => void;
  /** Saves transcript text as a new revision — the previous state is kept. */
  saveTranscript: (id: string, text: string) => void;
  /** Portal-only: bring an earlier saved transcript back as the current text. */
  restoreTranscriptRevision: (id: string, revisionId: string) => void;
  updateProject: (patch: Partial<Project>) => void;
  createProject: (input: {
    client: string;
    name: string;
    type: string;
    templateId: string;
  }) => void;
  duplicateProject: () => void;
  saveAsTemplate: (name: string) => void;
  addDeliverable: (input: NewDeliverableInput) => void;
  /** Copies a deliverable's setup as genuinely new work — no history carried. */
  duplicateDeliverable: (id: string) => void;
  updateDeliverable: (id: string, patch: Partial<Deliverable>) => void;
  removeDeliverable: (id: string) => void;
  /** Commit a new deliverable order, given the ids in their new sequence. */
  setDeliverableOrder: (ids: string[]) => void;
  approveDeliverable: (id: string) => void;
  requestChanges: (id: string, body: string, timecode?: string) => void;
  addNote: (id: string, author: 'Client' | 'Portal', body: string, timecode?: string) => void;
  publishVersion: (id: string, version: string) => void;
  resolveChangeRequest: (id: string, requestId: string) => void;
  updateShoot: (id: string, patch: Partial<Shoot>) => void;

  /** Client access management */
  canComment: boolean;
  canApprove: boolean;

  /** Portal team */
  /** Replaces the assignments held in the session with the saved ones. */
  setProjectTeam: (team: TeamMember[]) => void;
  addTeamMember: (input: {
    userId: string;
    name: string;
    role: string;
  }) => void;
  updateTeamMember: (id: string, patch: Partial<TeamMember>) => void;
  removeTeamMember: (id: string) => void;

  /** Activity + notifications */
  activity: Activity[];
  openActivity: (entry: Activity) => void;
  focusDeliverableId: string;
  needsAttention: NeedsAttention;
  clientAlerts: ClientAlert[];
  dismissAlert: (id: string) => void;
  toast: string | null;
  notify: (message: string) => void;
}
const PortalContext = createContext<PortalState | null>(null);

/** Date and time an action was taken, e.g. "26 Aug, 10:42 AM". */
function clock(): string {
  const now = new Date();
  const date = now.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short'
  });
  const time = now.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  return `${date}, ${time}`;
}
function stamp(): string {
  return new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).toUpperCase();
}
let seq = 0;
function uid(prefix: string): string {
  seq += 1;
  return `${prefix}-${Date.now()}-${seq}`;
}
function fromTemplate(row: TemplateDeliverable): Deliverable {
  return {
    id: uid('d'),
    name: row.name,
    type: row.type,
    quantity: '',
    /** New work always starts as Planned, whatever the template suggested. */
    status: 'Planned',
    version: '',
    versionDate: '—',
    runtime: '—',
    driveUrl: '',
    audioUrl: '',
    internalNote: '',
    visibleToClient: row.visibleToClient,
    notes: [],
    changeRequests: [],
    versions: []
  };
}
function versionLabel(status: Deliverable['status']): string {
  if (status === 'Changes Requested') return 'Changes Requested';
  if (status === 'Approved' || status === 'Delivered') return 'Approved';
  if (status === 'Ready for Review') return 'Reviewed';
  return 'First Cut';
}
function isReleased(item: Deliverable): boolean {
  return deliverableStage(item.status) === 'Final Delivery' && item.visibleToClient && item.status === 'Delivered';
}
export function PortalProvider({
  children
}: {
  children: React.ReactNode;
}) {
  /** A remembered name is all it takes to walk back into the project. */
  const [authed, setAuthed] = useState(() => Boolean(loadClientName()));
  const [viewMode, setViewModeState] = useState<ViewMode>('client');
  const [clientRoute, setClientRoute] = useState<ClientRoute>('home');
  const [portalRoute, setPortalRoute] = useState<PortalRoute>('projects');
  const [projects, setProjects] = useState<Project[]>(() => applyStoredTranscripts(applyStoredOrder(seedProjects)));
  const [templates, setTemplates] = useState<ProjectTemplate[]>(seedTemplates);
  const [activeProjectId, setActiveProjectId] = useState<string>('bloomberg');
  const [activeDeliverableId, setActiveDeliverableId] = useState<string>('d-ep02');
  /** Remembered in this browser, so the name is asked for only once. */
  const [clientName, setClientName] = useState<string>(() => loadClientName());
  const [activity, setActivity] = useState<Activity[]>(seedActivity);
  const [clientAlerts, setClientAlerts] = useState<ClientAlert[]>([]);
  const [focusDeliverableId, setFocusDeliverableId] = useState<string>('');
  const [previewAsClient, setPreviewAsClient] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [path, setPath] = useState<string>(() => typeof window === 'undefined' ? '/' : window.location.pathname);
  /** Restored on load so a refresh does not sign staff out mid-session. */
  const [staffSession, setStaffSession] = useState<StaffSession | null>(null);
  /** True until the persisted Supabase session has been checked. */
  const [staffChecking, setStaffChecking] = useState(true);

  /* Supabase persists the session, so a refresh restores it — provided the
     user is still in staff_users with an eligible role. */
  useEffect(() => {
    let live = true;
    void loadStaffSession().then(session => {
      if (live && session) setStaffSession(session);
    }).finally(() => {
      if (live) setStaffChecking(false);
    });
    return () => {
      live = false;
    };
  }, []);

  /* After that first check, Supabase's own auth events keep the session
     honest — a sign-out in another tab, or an expired refresh token. */
  useEffect(() => {
    if (!supabase) return;
    const {
      data
    } = supabase.auth.onAuthStateChange(event => {
      if (event === 'SIGNED_OUT') setStaffSession(null);
    });
    return () => data.subscription.unsubscribe();
  }, []);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  /**
   * A client link names its own project: /project/bloomberg-economy-of-scale.
   * The older /client?project=<id> form still works.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (slugFromPath(window.location.pathname)) return;
    const params = new URLSearchParams(window.location.search);
    const requested = params.get('project') || params.get('p');
    if (requested) setActiveProjectId(requested);
  }, []);

  /* Whatever project the current client link names is the project on screen. */
  useEffect(() => {
    const slug = slugFromPath(path);
    if (!slug) return;
    const matched = projects.find(item => item.slug === slug);
    if (matched) setActiveProjectId(matched.id);
  }, [path, projects]);
  const navigate = useCallback((next: string) => {
    if (typeof window !== 'undefined' && window.location.pathname !== next) {
      window.history.pushState({}, '', next);
    }
    setPath(next);
  }, []);
  const notify = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(current => current === message ? null : current), 2600);
  }, []);
  const patchProject = useCallback((projectId: string, patch: Partial<Project>) => {
    setProjects(list => list.map(item => item.id === projectId ? {
      ...item,
      ...patch,
      updated: stamp()
    } : item));
  }, []);
  const mapDeliverables = useCallback((projectId: string, fn: (list: Deliverable[]) => Deliverable[]) => {
    setProjects(list => list.map(item => item.id === projectId ? {
      ...item,
      deliverables: fn(item.deliverables),
      updated: stamp()
    } : item));
  }, []);

  /* With no projects yet, a blank one keeps every screen on its empty state. */
  const activeProject = projects.find(item => item.id === activeProjectId) ?? projects[0] ?? EMPTY_PROJECT;

  /**
   * Change requests and approvals are shared, so they are read back from
   * Supabase and merged into the project on screen. Local state stays the
   * source for everything else, and remains intact if the fetch is blocked.
   */
  const syncSharedReview = useCallback(async (project: Project, viewer: string) => {
    if (!supabaseConfigured) return;
    const [remoteRequests, remoteApprovals] = await Promise.all([fetchChangeRequests(project.slug, viewer), fetchApprovals(project.slug, viewer)]);
    if (!remoteRequests.length && !remoteApprovals.length) return;
    setProjects(list => list.map(item => {
      if (item.id !== project.id) return item;
      return {
        ...item,
        deliverables: item.deliverables.map(deliverable => {
          const requests = remoteRequests.filter(row => row.deliverableKey === deliverable.id);
          /* Stored rows are authoritative on `resolved`, so a request
             completed elsewhere stops counting as outstanding here. */
          const merged = [...deliverable.changeRequests.map(local => {
            const remote = requests.find(row => row.id === local.id);
            return remote ? {
              ...local,
              resolved: remote.resolved,
              shared: true
            } : local;
          }), ...requests.filter(row => !deliverable.changeRequests.some(local => local.id === row.id))];
          const approvalHere = remoteApprovals.find(row => row.deliverableKey === deliverable.id && (!row.version || row.version === deliverable.version));
          const approvedHere = Boolean(approvalHere);
          const outstanding = merged.some(row => row.version === deliverable.version && !row.resolved);

          /* Outstanding feedback outranks an approval; otherwise a stored
             approval of the current version means exactly that. */
          let status = deliverable.status;
          const inReview = deliverableStage(deliverable.status) === 'Review';
          if (outstanding && inReview) status = 'Changes Requested';else if (approvedHere && inReview) status = 'Approved';
          return {
            ...deliverable,
            changeRequests: merged,
            status,
            approvedBy: approvalHere?.displayName ?? deliverable.approvedBy,
            approvedAt: approvalHere?.at ?? deliverable.approvedAt
          };
        })
      };
    }));
  }, []);

  /**
   * Projects and deliverables come from the database — there is no seeded
   * client data. Also called straight after a project is created so the stored
   * rows, not the local copy, are what the studio sees.
   */
  const reloadProjects = useCallback(async () => {
    if (!supabaseConfigured) return;
    const rows = await fetchProjects();
    if (rows.length === 0) return;
    setProjects(current => {
      /* Anything created in this session and not yet in the database stays. */
      const local = current.filter(item => !rows.some(row => row.slug === item.slug));
      const merged = rows.map(row => {
        const held = current.find(item => item.slug === row.slug);
        if (!held) return row;
        /* Stored columns win; fields the schema has no column for (stage,
           Drive links, notes, requests, shoot details) stay as held. */
        return {
          ...held,
          ...row,
          id: row.id,
          type: held.type || row.type,
          description: held.description,
          nextShootDate: held.nextShootDate,
          nextShootTime: held.nextShootTime,
          location: held.location,
          clientContact: held.clientContact,
          producer: held.producer,
          callSheet: held.callSheet,
          shoots: held.shoots,
          team: held.team,
          deliverables: row.deliverables.map(item => {
            const local = held.deliverables.find(entry => entry.id === item.id);
            if (!local) return item;
            return {
              ...local,
              name: item.name,
              type: item.type,
              status: item.status,
              version: item.version,
              visibleToClient: item.visibleToClient
            };
          })
        };
      });
      return [...merged, ...local];
    });
  }, []);
  useEffect(() => {
    void reloadProjects();
  }, [staffSession, authed, reloadProjects]);

  /**
   * The client side never lists projects — it loads the one its link grants
   * access to, by the real id join_project returns.
   */
  const loadClientProject = useCallback(async (id: string) => {
    const project = await fetchProjectById(id);
    if (!project) return;
    setProjects(list => {
      const held = list.find(item => item.slug === project.slug);
      if (!held) return [project, ...list];
      return list.map(item => item.slug === project.slug ? {
        ...held,
        ...project,
        deliverables: project.deliverables.map(entry => {
          const local = held.deliverables.find(row => row.id === entry.id);
          return local ? {
            ...local,
            ...entry
          } : entry;
        })
      } : item);
    });
    setActiveProjectId(project.id);
  }, []);

  /* A remembered viewer returns straight into the project, so the link's
     project is opened and loaded without another name prompt. */
  useEffect(() => {
    const slug = slugFromPath(path);
    if (!authed || !slug || !supabaseConfigured) return;
    let live = true;
    void ensureAnonSession().then(() => openProjectSession(slug, clientName || 'Client')).then(id => {
      if (live && id) void loadClientProject(id);
    });
    return () => {
      live = false;
    };
  }, [authed, path, clientName, loadClientProject]);

  /* Studio activity comes from the stored change requests and approvals. */
  useEffect(() => {
    if (!staffSession || !supabaseConfigured) return;
    let live = true;
    void fetchStaffActivity().then(rows => {
      if (!live || rows.length === 0) return;
      setActivity(rows.map(row => {
        const project = projects.find(item => item.slug === row.projectSlug);
        return {
          id: row.id,
          projectId: project?.id ?? row.projectSlug,
          projectName: row.projectName || (project?.client ?? ''),
          actorName: row.actorName,
          action: row.action,
          deliverableId: row.deliverableKey,
          message: row.message,
          at: row.at
        };
      }));
    });
    /* The dashboard summarises every project, so each one is reconciled with
       its stored requests and approvals rather than only the open one. */
    projects.filter(item => !item.archived).forEach(item => {
      void syncSharedReview(item, '');
    });
    return () => {
      live = false;
    };
    // Refreshed each time a staff session is established, i.e. on load.
  }, [staffSession]);

  /* Pick up anything submitted from another browser or device. */
  useEffect(() => {
    void syncSharedReview(activeProject, clientName);
  }, [activeProject.id, activeProject.slug, clientName, syncSharedReview]);
  const value = useMemo<PortalState>(() => {
    const projectId = activeProject.id;
    /* One shared link per project: whoever is at the keyboard is named here. */
    const viewerName = previewAsClient ? staffSession?.name ?? 'Portal' : clientName.trim() || 'Client';
    const log = (actorName: string, action: string, deliverableId: string) => {
      setActivity(list => [{
        id: uid('act'),
        projectId,
        projectName: activeProject.client,
        actorName,
        action,
        deliverableId,
        at: Date.now()
      }, ...list]);
    };
    const patchDeliverable = (id: string, patch: Partial<Deliverable>) => {
      const before = activeProject.deliverables.find(item => item.id === id);
      mapDeliverables(projectId, list => list.map(item => item.id === id ? {
        ...item,
        ...patch
      } : item));
      if (before) {
        const after = {
          ...before,
          ...patch
        };
        if (!isReleased(before) && isReleased(after)) {
          setClientAlerts(list => [...list, {
            id: uid('alert'),
            projectId,
            kind: 'files',
            title: 'Final files are ready',
            body: `${after.name} is available to download.`,
            actionLabel: 'View Files',
            deliverableId: id
          }]);
        }
      }
    };
    const appendNote = (id: string, author: 'Client' | 'Portal' | 'System', body: string, authorName?: string, extra: Partial<Deliverable> = {}, timecode?: string) => mapDeliverables(projectId, list => list.map(item => item.id === id ? {
      ...item,
      ...extra,
      notes: [...item.notes, {
        id: uid('n'),
        author,
        authorName,
        timecode,
        time: clock(),
        body
      }]
    } : item));
    return {
      authed,
      clientName,
      signIn: (name: string) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        saveClientName(trimmed);
        setClientName(trimmed);
        /* Establishes the anonymous session and exchanges the slug + access
           token on this link for the project. The client never sees a login. */
        void ensureAnonSession().then(() => openProjectSession(slugFromPath(path) || activeProject.slug, trimmed)).then(id => id ? loadClientProject(id) : null);
        setAuthed(true);
        setViewModeState('client');
        setClientRoute('home');
      },
      changeClientName: () => {
        closeProjectSession();
        clearClientName();
        setClientName('');
        setAuthed(false);
        setClientRoute('home');
      },
      path,
      navigate,
      inStaffArea: path.startsWith(STAFF_PATH) || path.startsWith(ADMIN_PATH),
      staffAuthed: Boolean(staffSession),
      staffChecking,
      staffUser: staffSession,
      staffSignIn: async (email, password) => {
        const result = await authenticateStaff(email, password);
        if (result.ok && result.session) {
          setStaffSession(result.session);
          setViewModeState('portal');
          setPortalRoute('projects');
          setPreviewAsClient(false);
          navigate(ADMIN_PATH);
        }
        return result;
      },
      staffSignOut: async () => {
        await clearStaffSession();
        setStaffSession(null);
        setPreviewAsClient(false);
        setPortalRoute('projects');
        navigate(STAFF_PATH);
      },
      staffResetPassword: email => requestStaffPasswordReset(email),
      staffVerifyRecoveryCode: (email, code) => verifyStaffRecoveryCode(email, code),
      staffCompletePasswordReset: (password, confirm) => completeStaffPasswordReset(password, confirm),
      staffChangePassword: (current, password, confirm) => staffSession ? changeStaffPassword(staffSession.email, current, password, confirm) : Promise.resolve({
        ok: false,
        error: 'Not signed in.'
      }),
      staffCheckInvite: () => establishInviteSession(),
      staffAcceptInvite: async (password, confirm) => {
        const result = await completeStaffInvite(password, confirm);
        if (result.ok && result.session) {
          setStaffSession(result.session);
          setViewModeState('portal');
          setPortalRoute('projects');
          setPreviewAsClient(false);
          navigate(ADMIN_PATH);
        }
        return result;
      },
      goStaffAccess: () => {
        setPortalRoute('projects');
        navigate(STAFF_PATH);
      },
      viewMode,
      setViewMode: mode => {
        setViewModeState(mode);
        if (mode === 'portal') {
          setPortalRoute('projects');
          navigate(staffSession ? ADMIN_PATH : STAFF_PATH);
        } else {
          setClientRoute('home');
          navigate(CLIENT_PATH);
        }
      },
      clientRoute,
      goClient: setClientRoute,
      portalRoute,
      goPortal: setPortalRoute,
      projects,
      activeProjects: projects.filter(item => !item.archived),
      archivedProjects: projects.filter(item => item.archived),
      activeProject,
      openProject: id => {
        setActiveProjectId(id);
        setFocusDeliverableId('');
        setPortalRoute('project');
      },
      templates,
      previewAsClient,
      /* Opens the project through its own secure link, so the preview shows
         exactly what the token grants a client — nothing held locally. */
      startPreview: () => {
        setPreviewAsClient(true);
        setClientRoute('home');
        void fetchAccessToken(activeProject.slug, projectId).then(async token => {
          if (!token) return;
          const id = await openProjectSession(activeProject.slug, 'Portal Preview', token);
          if (id) await loadClientProject(id);
        });
      },
      endPreview: () => {
        setPreviewAsClient(false);
        setPortalRoute('project');
      },
      projectComplete: activeProject.archived || projectStatus(activeProject) === 'Complete',
      /* Archiving only changes the project's status — every deliverable,
         change request, approval, activity record and its access token stay
         exactly as they are. Nothing is ever deleted. */
      archiveProject: () => {
        patchProject(projectId, {
          archived: true,
          status: 'Delivered'
        });
        void updateProjectRow(projectId, {
          archived: true
        }).then(result => {
          if (!result.ok) notify(`Not archived · ${result.error}`);
        });
      },
      /* Permanent, and only for test, duplicate or mistaken projects. The whole
         deletion happens in one database transaction, so the project is only
         removed from the interface once Supabase confirms it succeeded. */
      deleteProject: async () => {
        const {
          slug,
          client,
          name
        } = activeProject;
        const result = await deleteProjectRow(projectId, slug);
        if (!result.ok) {
          notify(result.error ?? 'Project could not be deleted. Nothing was removed.');
          return false;
        }
        const remaining = projects.filter(item => item.id !== projectId);
        setProjects(remaining);
        setActiveProjectId(remaining[0]?.id ?? '');
        setActiveDeliverableId('');
        setPortalRoute('projects');
        notify(`Project deleted · ${client} — ${name}`);
        return true;
      },
      /** An active project with the same client and project name, if one exists. */
      findExistingProject: (client, name) => {
        const key = (value: string) => value.trim().toLowerCase();
        return projects.find(item => !item.archived && key(item.client) === key(client) && key(item.name) === key(name)) ?? null;
      },
      reopenProject: id => {
        setProjects(list => list.map(item => item.id === id ? {
          ...item,
          archived: false,
          status: 'In Progress'
        } : item));
        void updateProjectRow(id, {
          archived: false
        }).then(result => {
          if (!result.ok) notify(`Not restored · ${result.error}`);
        });
      },
      toggleVersionVisibility: (deliverableId, versionId) => mapDeliverables(projectId, list => list.map(item => item.id === deliverableId ? {
        ...item,
        versions: item.versions.map(entry => entry.id === versionId ? {
          ...entry,
          visibleToClient: !entry.visibleToClient
        } : entry)
      } : item)),
      contactPortal: body => {
        const who = viewerName;
        setActivity(list => [{
          id: uid('act'),
          projectId,
          projectName: activeProject.client,
          actorName: who,
          action: 'sent a message to Portal',
          message: body,
          deliverableId: '',
          at: Date.now()
        }, ...list]);
      },
      clientDeliverables: activeProject.deliverables.filter(item => item.visibleToClient),
      reviewDeliverables: activeProject.deliverables.filter(item => item.visibleToClient && deliverableStage(item.status) === 'Review'),
      finalDeliverables: activeProject.deliverables.filter(item => item.visibleToClient && deliverableStage(item.status) === 'Final Delivery'),
      activeDeliverable: activeProject.deliverables.find(item => item.id === activeDeliverableId),
      openDeliverable: id => {
        setActiveDeliverableId(id);
        const item = activeProject.deliverables.find(entry => entry.id === id);
        setClientRoute(item?.type === 'Transcript' ? 'transcript' : 'episode');
      },
      /**
       * Saving never overwrites: the state being replaced is kept as a
       * revision, so an earlier version can always be restored.
       */
      saveTranscript: (id, text) => {
        const current = activeProject.deliverables.find(entry => entry.id === id);
        if (!current) return;
        const history = current.transcriptRevisions?.length ? current.transcriptRevisions : [{
          id: uid('tr'),
          text: current.transcript ?? '',
          editorName: 'Portal',
          at: Date.now()
        }];

        // The editor is whoever is at the keyboard: a staff member previewing
        // the client view, otherwise the name this browser gave.
        const editorName = viewerName;
        const revisions = [...history, {
          id: uid('tr'),
          text,
          editorName,
          at: Date.now()
        }];
        patchDeliverable(id, {
          transcript: text,
          transcriptRevisions: revisions
        });
        saveTranscriptText(id, text, revisions);
      },
      restoreTranscriptRevision: (id, revisionId) => {
        const current = activeProject.deliverables.find(entry => entry.id === id);
        const target = current?.transcriptRevisions?.find(entry => entry.id === revisionId);
        if (!current || !target) return;
        const revisions = [...(current.transcriptRevisions ?? []), {
          id: uid('tr'),
          text: target.text,
          editorName: 'Portal',
          at: Date.now()
        }];
        patchDeliverable(id, {
          transcript: target.text,
          transcriptRevisions: revisions
        });
        saveTranscriptText(id, target.text, revisions);
      },
      updateProject: patch => {
        patchProject(projectId, patch);
        void updateProjectRow(projectId, patch).then(result => {
          if (!result.ok) notify(`Not saved · ${result.error}`);
        });
        const touchesShoot = patch.nextShootDate !== undefined || patch.nextShootTime !== undefined || patch.location !== undefined;
        if (!touchesShoot) return;
        setProjects(list => list.map(item => {
          if (item.id !== projectId) return item;
          const date = patch.nextShootDate ?? item.nextShootDate;
          const time = patch.nextShootTime ?? item.nextShootTime;
          const location = patch.location ?? item.location;
          if (item.shoots.length === 0) {
            if (!date) return item;
            return {
              ...item,
              shoots: [{
                id: uid('shoot'),
                date,
                title: 'Shoot Day',
                time,
                city: '',
                location,
                details: []
              }]
            };
          }
          return {
            ...item,
            shoots: item.shoots.map((shoot, index) => index === 0 ? {
              ...shoot,
              date: date || shoot.date,
              time,
              location
            } : shoot)
          };
        }));
      },
      createProject: ({
        client,
        name,
        type,
        templateId
      }) => {
        const template = templates.find(item => item.id === templateId);
        const id = uid('proj');
        const created: Project = {
          id,
          /* The client link is minted once here and never changes. */
          slug: uniqueSlug(client, name, projects.map(item => item.slug)),
          client,
          name,
          type: type || template?.projectType || 'Production',
          description: '',
          status: 'Planned',
          nextShootDate: '',
          nextShootTime: '',
          location: '',
          clientContact: '',
          producer: 'Portal Productions',
          updated: stamp(),
          deliverables: (template?.deliverables ?? []).map(fromTemplate),
          shoots: [],
          team: [],
          archived: false
        };
        setProjects(list => [created, ...list]);
        setActiveProjectId(id);
        setPortalRoute('project');

        /* Stored immediately, then re-keyed to the database's own UUID so the
           project and its link are identical in every browser. */
        void insertProject(created).then(async ({
          result,
          created: row
        }) => {
          if (!result.ok) {
            /* Two staff creating the same project at once: the database refuses
               the duplicate, so the existing project is opened instead. */
            if (result.error?.includes('23505')) {
              setProjects(list => list.filter(item => item.id !== id));
              const rows = await fetchProjects();
              const existing = rows.find(item => item.slug === created.slug);
              await reloadProjects();
              if (existing) {
                setActiveProjectId(existing.id);
                setPortalRoute('project');
                notify('This project already exists · opened it instead');
                return;
              }
            }
            notify(`Not saved · ${result.error}`);
            return;
          }
          if (!row) return;
          setProjects(list => list.map(item => item.id === id ? {
            ...item,
            id: row.id
          } : item));
          setActiveProjectId(current => current === id ? row.id : current);
          /* Read the stored rows back so the project page shows what exists. */
          await reloadProjects();
        });
      },
      duplicateProject: () => {
        const id = uid('proj');
        const copy: Project = {
          ...activeProject,
          id,
          slug: uniqueSlug(`${activeProject.client} Copy`, activeProject.name, projects.map(item => item.slug)),
          client: `${activeProject.client} (Copy)`,
          status: 'Planned',
          updated: stamp(),
          deliverables: activeProject.deliverables.map(item => ({
            ...item,
            id: uid('d'),
            /** A duplicate is new work, so it starts at the beginning again. */
            status: 'Planned',
            version: '',
            versionDate: '—',
            driveUrl: '',
            audioUrl: '',
            notes: [],
            changeRequests: [],
            versions: []
          })),
          shoots: [],
          /* Assignments belong to the original project's rows, so a duplicate
             starts unassigned rather than showing team that was never saved. */
          team: [],
          archived: false
        };
        setProjects(list => [copy, ...list]);
        setActiveProjectId(id);
        setPortalRoute('project');
      },
      saveAsTemplate: name => {
        setTemplates(list => [...list, {
          id: uid('tpl'),
          name,
          projectType: activeProject.type,
          deliverables: activeProject.deliverables.map(item => ({
            name: item.name,
            type: item.type,
            status: 'Planned',
            visibleToClient: item.visibleToClient
          }))
        }]);
      },
      addDeliverable: input => {
        const newId = uid('d');
        const order = activeProject.deliverables.length;
        mapDeliverables(projectId, list => [...list, {
          id: newId,
          name: input.name,
          type: input.type,
          quantity: '',
          status: input.status,
          version: input.version,
          versionDate: input.version ? stamp() : '—',
          runtime: '—',
          driveUrl: input.driveUrl,
          audioUrl: input.audioUrl,
          videoFormat: input.type === 'Video' ? input.videoFormat : undefined,
          internalNote: '',
          visibleToClient: input.visibleToClient,
          transcript: input.transcript,
          notes: [],
          changeRequests: [],
          versions: []
        }]);
        void insertDeliverable(projectId, activeProject.slug, {
          id: newId,
          name: input.name,
          type: input.type,
          quantity: '',
          status: input.status,
          version: input.version,
          versionDate: '—',
          runtime: '—',
          driveUrl: input.driveUrl,
          audioUrl: input.audioUrl,
          internalNote: '',
          visibleToClient: input.visibleToClient,
          notes: [],
          changeRequests: [],
          versions: []
        }, order).then(async result => {
          if (!result.ok) {
            notify(`Not saved · ${result.error}`);
            return;
          }
          await reloadProjects();
        });
      },
      /**
       * Copies the useful setup — name, type, framing, visibility — but never
       * the client's feedback, approvals or version history. The copy is new
       * work with its own id, starting at the beginning again.
       */
      duplicateDeliverable: id => {
        const source = activeProject.deliverables.find(item => item.id === id);
        if (!source) return;
        const newId = uid('d');
        const order = activeProject.deliverables.length;
        const copy: Deliverable = {
          ...source,
          id: newId,
          name: `${source.name} (Copy)`,
          status: 'Planned',
          version: '',
          versionDate: '—',
          runtime: '—',
          driveUrl: '',
          audioUrl: '',
          visibleToClient: false,
          transcript: undefined,
          transcriptRevisions: undefined,
          notes: [],
          changeRequests: [],
          versions: [],
          approvedBy: undefined,
          approvedAt: undefined
        };
        mapDeliverables(projectId, list => [...list, copy]);
        void insertDeliverable(projectId, activeProject.slug, copy, order).then(async result => {
          if (!result.ok) {
            notify(`Not saved · ${result.error}`);
            return;
          }
          await reloadProjects();
        });
      },
      updateDeliverable: (id, patch) => {
        patchDeliverable(id, patch);
        void updateDeliverableRow(projectId, id, patch).then(result => {
          if (!result.ok) notify(`Not saved · ${result.error}`);
        });
      },
      removeDeliverable: id => {
        mapDeliverables(projectId, list => list.filter(item => item.id !== id));
        void deleteDeliverableRow(projectId, id).then(result => {
          if (!result.ok) notify(`Not removed · ${result.error}`);
        });
      },
      setDeliverableOrder: ids => mapDeliverables(projectId, list => {
        const next = ids.map(id => list.find(item => item.id === id)).filter((item): item is Deliverable => Boolean(item));
        if (next.length !== list.length) return list;
        saveDeliverableOrder(projectId, ids);
        void saveDeliverableOrderRows(projectId, ids);
        return next;
      }),
      approveDeliverable: id => {
        const current = activeProject.deliverables.find(item => item.id === id);
        const who = viewerName;
        /* Recorded so the studio can see who approved the current version. */
        mapDeliverables(projectId, list => list.map(item => item.id === id ? {
          ...item,
          approvedBy: who,
          approvedAt: Date.now()
        } : item));
        appendNote(id, 'System', `${who} approved ${current?.version || 'this version'}`, undefined, {
          status: 'Approved'
        });
        log(who, `approved ${current?.name ?? 'a deliverable'}`, id);

        /* Shared: the approval must be visible on every other device. */
        void insertApproval({
          projectSlug: activeProject.slug,
          deliverableKey: id,
          displayName: who,
          version: current?.version ?? ''
        });
      },
      /**
       * Ongoing: anyone on the client team can keep adding changes against the
       * current version. Only the first one raises a status event.
       */
      requestChanges: (id, body, timecode) => {
        const who = viewerName;
        const current = activeProject.deliverables.find(item => item.id === id);
        const version = current?.version ?? '';
        const localId = uid('cr');
        const code = normaliseTimecode(timecode ?? '');
        const firstForVersion = !current?.changeRequests.some(request => request.version === version && !request.resolved);
        mapDeliverables(projectId, list => list.map(item => item.id === id ? {
          ...item,
          status: 'Changes Requested',
          notes: firstForVersion ? [...item.notes, {
            id: uid('n'),
            author: 'System',
            time: clock(),
            body: `Changes requested on ${item.version || 'this version'}`
          }] : item.notes,
          changeRequests: [...item.changeRequests, {
            id: localId,
            body,
            timecode: code,
            authorName: who,
            time: clock(),
            submitted: `${stamp()} · ${clock()}`,
            at: Date.now(),
            version,
            resolved: false
          }]
        } : item));
        if (firstForVersion) {
          log(who, `requested changes to ${current?.name ?? 'a deliverable'}`, id);
        }

        /* Shared: stored once, seen by everyone holding the project link. The
           local row adopts the stored id so it is never duplicated on reload. */
        void insertChangeRequest({
          projectSlug: activeProject.slug,
          deliverableKey: id,
          displayName: who,
          comment: body,
          timecode: code,
          version
        }).then(remoteId => {
          if (!remoteId) return;
          mapDeliverables(projectId, list => list.map(item => item.id === id ? {
            ...item,
            changeRequests: item.changeRequests.map(request => request.id === localId ? {
              ...request,
              id: remoteId,
              shared: true
            } : request)
          } : item));
        });
      },
      addNote: (id, author, body, timecode) => {
        const who = author === 'Client' ? viewerName : 'Portal';
        appendNote(id, author, body, who, {}, normaliseTimecode(timecode ?? ''));
        if (author === 'Client') {
          const current = activeProject.deliverables.find(item => item.id === id);
          log(who, `commented on ${current?.name ?? 'a deliverable'}`, id);
        }
      },
      publishVersion: (id, version) => {
        const current = activeProject.deliverables.find(item => item.id === id);
        const next = version.trim() || current?.version || 'V1';
        mapDeliverables(projectId, list => list.map(item => {
          if (item.id !== id) return item;
          const superseded = item.version && item.version !== next ? [{
            id: uid('v'),
            version: item.version,
            label: versionLabel(item.status),
            date: item.versionDate,
            driveUrl: item.driveUrl,
            changeCount: item.changeRequests.filter(request => request.version === item.version).length,
            visibleToClient: false
          }, ...item.versions] : item.versions;
          return {
            ...item,
            version: next,
            versionDate: stamp(),
            status: 'Ready for Review',
            versions: superseded,
            /* A new cut is unapproved until the client says otherwise. */
            approvedBy: undefined,
            approvedAt: undefined,
            changeRequests: item.changeRequests.map(request => ({
              ...request,
              resolved: true
            })),
            notes: [...item.notes, {
              id: uid('n'),
              author: 'System',
              time: clock(),
              body: `Portal published ${next}`
            }]
          };
        }));
        /**
         * Only claim feedback has been addressed when the client actually asked
         * for changes on the version being replaced.
         */
        const hadFeedback = Boolean(current && (current.status === 'Changes Requested' || current.changeRequests.some(request => !request.resolved)));
        const firstCut = !current || current.versions.length === 0;
        setClientAlerts(list => [...list.filter(alert => alert.deliverableId !== id), {
          id: uid('alert'),
          projectId,
          kind: 'review',
          title: `${(current?.name ?? 'New cut').toUpperCase()} — ${next} is ready`,
          body: hadFeedback ? 'Your requested changes have been addressed.' : firstCut ? 'Your first cut is ready for review.' : 'A new version is ready for review.',
          actionLabel: `Review ${next}`,
          deliverableId: id
        }]);
        log('Portal', `published ${next} of ${current?.name ?? 'a deliverable'}`, id);
      },
      /* Staff-only. The stored row is updated, never deleted, so the completed
         request stays on the record for both sides. */
      resolveChangeRequest: (id, requestId) => {
        const completedAt = new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        });
        const completedBy = staffSession?.name.split(' ')[0] ?? 'Portal';
        mapDeliverables(projectId, list => list.map(item => item.id === id ? {
          ...item,
          changeRequests: item.changeRequests.map(request => request.id === requestId ? {
            ...request,
            resolved: true,
            completedAt,
            completedBy
          } : request)
        } : item));
        void resolveRemoteChangeRequest(requestId);
      },
      updateShoot: (id, patch) => setProjects(list => list.map(item => item.id === projectId ? {
        ...item,
        shoots: item.shoots.map(shoot => shoot.id === id ? {
          ...shoot,
          ...patch
        } : shoot)
      } : item)),
      /* One shared link: everyone who has it can comment and approve. */
      canComment: true,
      canApprove: true,
      /* project_staff is the source of truth. Replaces whatever the session
         held with the saved assignments whenever the project is opened. */
      setProjectTeam: team => patchProject(projectId, {
        team
      }),
      /* Nothing is shown until project_staff confirms the row exists. */
      addTeamMember: ({
        userId,
        name,
        role
      }) => {
        if (activeProject.team.some(member => member.id === userId)) return;
        void assignProjectMember(activeProject.id, activeProject.slug, userId, role).then(result => {
          if (!result.ok) {
            notify('Team assignment could not be saved.');
            return;
          }
          patchProject(projectId, {
            team: [...activeProject.team, {
              id: userId,
              name,
              role
            }]
          });
        });
      },
      updateTeamMember: (id, patch) => {
        const previous = activeProject.team;
        patchProject(projectId, {
          team: previous.map(member => member.id === id ? {
            ...member,
            ...patch
          } : member)
        });
        if (patch.role === undefined) return;
        void updateProjectMemberRole(activeProject.id, activeProject.slug, id, patch.role).then(result => {
          if (result.ok) return;
          patchProject(projectId, {
            team: previous
          });
          notify('Team assignment could not be saved.');
        });
      },
      /* Anyone assigned can be removed — current user, owner, creator alike. */
      removeTeamMember: id => {
        const previous = activeProject.team;
        void removeProjectMember(activeProject.id, activeProject.slug, id).then(result => {
          if (!result.ok) {
            // They stay on screen, because they are still in the database.
            notify('Team assignment could not be removed.');
            return;
          }
          patchProject(projectId, {
            team: previous.filter(member => member.id !== id)
          });
        });
      },
      activity,
      openActivity: entry => {
        setActiveProjectId(entry.projectId);
        setFocusDeliverableId(entry.deliverableId);
        setPortalRoute('project');
      },
      focusDeliverableId,
      needsAttention: projects.reduce<NeedsAttention>((totals, item) => {
        if (item.archived) return totals;
        item.deliverables.forEach(deliverable => {
          if (deliverable.status === 'Ready for Review' && deliverable.visibleToClient) {
            totals.reviewsWaiting += 1;
          }
          totals.changeRequests += deliverable.changeRequests.filter(request => !request.resolved).length;
          if ((deliverable.status === 'In Progress' || deliverable.status === 'Changes Requested') && deliverable.changeRequests.every(request => request.resolved)) {
            totals.readyToPublish += 1;
          }
        });
        return totals;
      }, {
        reviewsWaiting: 0,
        changeRequests: 0,
        readyToPublish: 0
      }),
      clientAlerts: clientAlerts.filter(alert => alert.projectId === projectId),
      dismissAlert: id => setClientAlerts(list => list.filter(alert => alert.id !== id)),
      toast,
      notify
    };
  }, [authed, path, navigate, staffSession, staffChecking, reloadProjects, loadClientProject, viewMode, clientRoute, portalRoute, projects, templates, activeProject, activeDeliverableId, clientName, activity, clientAlerts, focusDeliverableId, previewAsClient, toast, notify, patchProject, mapDeliverables]);
  return <PortalContext.Provider value={value} data-id="element-445" data-mp="1el73hdjuz2ld">{children}</PortalContext.Provider>;
}
export function usePortal(): PortalState {
  const context = useContext(PortalContext);
  if (!context) throw new Error('usePortal must be used inside PortalProvider');
  return context;
}